import { Shipment } from '../models/shipment';
import { Card } from '../models/card';
import { Package } from '../models/package';
import { Commodity } from '../models/commodity';

function cardHash(card: Card): string {
    return [
        'attn:' + card.attn || '',
        'org:' + card.organization || '',
        'country:' + card.country || '',
        'city:' + card.city || '',
        'postalcode:' + card.postalCode || '',
        'province:' + card.province || '',
        'street1:' + card.street1 || '',
        'street2:' + card.street2 || '',
        'street3:' + card.street3 || '',
        'tel:' + card.telephone || '',
        'email:' + card.email || '',
        'res:' + card.residentialFlag ? '1' : '0',
        'open:' + card.openTime || '',
        'close:' + card.closeTime || ''
    ].join(',');
}

function commodityHash(commodity: Commodity): string {
    return [
        'desc:' + commodity.description || '',
        'hs:' + commodity.harmonizedCode || '',
        'qty:' + commodity.quantity?.toString() || '0',
        'price:' + commodity.price?.toString() || '0',
        'unit:' + commodity.unitOfMeasure || '',
        'weight:' + commodity.weight?.toString() || '0'
    ].join(',');
}

function packageHash(pkg: Package): string {
    return [
        'desc:' + pkg.description || '',
        'qty:' + pkg.quantity?.toString() || '0',
        'weight:' + pkg.weight?.toString() || '0',
        'len:' + pkg.length?.toString() || '0',
        'wid:' + pkg.width?.toString() || '0',
        'hgt:' + pkg.height?.toString() || '0',
        'ins:' + pkg.value?.toString() || '',
        'fc:' + pkg.freightClass || '',
        'pc:' + pkg.packageCode || '',
        'st:' + pkg.stackable,
        'hc:' + pkg.hazmatClass || '',
        'un:' + pkg.unNumber || '',
        'commodities:(' + pkg.commodities.map(c => commodityHash(c)).join(',') + ')'
    ].join(',');
}

function shipmentToString(shipment: Shipment): string {
    return [
        'shipper:(' + cardHash(shipment.shipper) + ')',
        'destination:(' + cardHash(shipment.destination) + ')',
        'packages:(' + shipment.packages.map(p => packageHash(p)).join(',') + ')',
        'pkgtype:' + shipment.packagingType || '',
        'currency:' + shipment.currency || '',
        'date:' + shipment.shipmentDate || '',
        'payor:' + shipment.paymentOption + '/' + shipment.paymentAccountNumber || '',
        'signature:' + shipment.signatureOption || '',
        'options:' + Object.values(shipment.courierOptions).join(',') || '',
        'references:' + Object.values(shipment.references).join(',') || '',
    ].join(',');
}

/*
    cyrb53 (c) 2018 bryc (github.com/bryc)
    License: Public domain (or MIT if needed). Attribution appreciated.
    A fast and simple 53-bit string hash function with decent collision resistance.
    Largely inspired by MurmurHash2/3, but with a focus on speed/simplicity.
*/
const cyrb53 = function(str, seed = 0) {
    let h1 = 0xdeadbeef ^ seed, h2 = 0x41c6ce57 ^ seed;
    for(let i = 0, ch; i < str.length; i++) {
        ch = str.charCodeAt(i);
        h1 = Math.imul(h1 ^ ch, 2654435761);
        h2 = Math.imul(h2 ^ ch, 1597334677);
    }
    h1  = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
    h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2  = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
    h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
    return 4294967296 * (2097151 & h2) + (h1 >>> 0);
};

export function shipmentHash(shipment: Shipment): string {
    return cyrb53(shipmentToString(shipment)).toString(16);
}
